import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check, Close } from '@mui/icons-material';
import { QuestionComponentProps } from '../../types';
import { CommentField } from '../CommentField';

export const YesNoQuestion: React.FC<QuestionComponentProps> = ({
  question,
  value,
  comment,
  language,
  onChange,
  onCommentChange,
  disabled = false,
  error,
  autoFocus = false,
}) => {
  const getLocalizedText = (textRecord: Record<string, string>): string => {
    return textRecord[language] || textRecord['en'] || textRecord[Object.keys(textRecord)[0]] || '';
  };

  const handleYesClick = () => {
    if (!disabled) {
      onChange(true);
    }
  };

  const handleNoClick = () => {
    if (!disabled) {
      onChange(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, answer: boolean) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!disabled) {
        onChange(answer);
      }
    }
  };

  const getYesText = (): string => {
    return language === 'no' ? 'Ja' : 'Yes';
  };

  const getNoText = (): string => {
    return language === 'no' ? 'Nei' : 'No';
  };

  const isYesSelected = value === true;
  const isNoSelected = value === false;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: 'center', mb: 2 }}>
        {getLocalizedText(question.title)}
      </Typography>

      {question.description && (
        <Typography sx={{ textAlign: 'center', mb: 4, maxWidth: 500, color: 'text.secondary' }}>
          {getLocalizedText(question.description)}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={handleYesClick}
          onKeyDown={(e) => handleKeyDown(e, true)}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional: focus first button for keyboard navigation in survey flow
          autoFocus={autoFocus}
          startIcon={<Check />}
          aria-label={`${getYesText()} - ${getLocalizedText(question.title)}`}
          aria-pressed={isYesSelected}
          data-testid="yes-button"
          sx={{
            minWidth: 120,
            minHeight: 60,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: isYesSelected ? 'primary.main' : 'divider',
            bgcolor: isYesSelected
              ? (theme) => alpha(theme.palette.primary.main, 0.12)
              : 'background.paper',
            color: isYesSelected ? 'primary.main' : 'text.primary',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: disabled
                ? undefined
                : isYesSelected
                  ? (theme) => alpha(theme.palette.primary.main, 0.12)
                  : 'action.hover',
              borderColor: isYesSelected ? 'primary.main' : 'divider',
            },
          }}
        >
          {getYesText()}
        </Button>

        <Button
          variant="outlined"
          size="large"
          onClick={handleNoClick}
          onKeyDown={(e) => handleKeyDown(e, false)}
          disabled={disabled}
          startIcon={<Close />}
          aria-label={`${getNoText()} - ${getLocalizedText(question.title)}`}
          aria-pressed={isNoSelected}
          data-testid="no-button"
          sx={{
            minWidth: 120,
            minHeight: 60,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: isNoSelected ? 'primary.main' : 'divider',
            bgcolor: isNoSelected
              ? (theme) => alpha(theme.palette.primary.main, 0.12)
              : 'background.paper',
            color: isNoSelected ? 'primary.main' : 'text.primary',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: disabled
                ? undefined
                : isNoSelected
                  ? (theme) => alpha(theme.palette.primary.main, 0.12)
                  : 'action.hover',
              borderColor: isNoSelected ? 'primary.main' : 'divider',
            },
          }}
        >
          {getNoText()}
        </Button>
      </Box>

      {question.hasCommentField && onCommentChange && (
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          <CommentField
            value={comment}
            onChange={onCommentChange}
            language={language}
            disabled={disabled}
            placeholder={
              question.commentFieldDescription
                ? getLocalizedText(question.commentFieldDescription)
                : undefined
            }
          />
        </Box>
      )}

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{ textAlign: 'center', mt: 2 }}
          role="alert"
          data-testid="error-message"
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
